import { Dispatch, RefObject, SetStateAction, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { selectPlayerConfig } from 'store/player/selectors';
import {
    endSentryTransaction,
    startSentryTransaction,
} from 'store/sentry/actions.ts';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants.ts';

import { VideoItem } from 'services/data-manager/types';
import useGetPlaybackPauseUavParams from 'services/uav/useGetPlaybackPauseUavParams.ts';
import useGetPlaybackResumeUavParams from 'services/uav/useGetPlaybackResumeUavParams';
import { logUavAnalytics } from 'services/uav/utils.ts';

import {
    PlayerControlsAccessLevel,
    accessIsRestricted,
} from 'screens/PlayerModal/hooks/useTrickModeControl.ts';

import { PlayerRef } from 'components/Player/types.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem: VideoItem | undefined;
    playerPaused: boolean;
    setPlayerPausedByUser: Dispatch<SetStateAction<boolean>>;
    setPlayerPaused: Dispatch<SetStateAction<boolean>>;
    setReadyToPlay: Dispatch<SetStateAction<boolean>>;
    pauseAccessLevel: PlayerControlsAccessLevel;
    isLive: boolean;
    isSOTSPauseDisabled: boolean;
    playerProgress: number;
    timeshiftAllowed: boolean;
    startoverAllowed: boolean;
    templateId?: string;
    railId?: string;
    onShowControlsRestrictedNotification(): void;
    onShowTimeshiftNotAvailableNotification(): void;
    onSetTimeshiftNotification(): void;
    onSwitchTimeshiftModeOn(progress: number, isUserPaused: boolean): void;
    onPauseLiveStream(progress: number): void;
    onUpdateBookmark(): void;
};

function usePlayerPlayPause({
    playerRef,
    playerPaused,
    videoItem,
    setPlayerPausedByUser,
    setPlayerPaused,
    setReadyToPlay,
    pauseAccessLevel,
    isLive,
    isSOTSPauseDisabled,
    playerProgress,
    timeshiftAllowed,
    startoverAllowed,
    templateId,
    railId,
    onShowControlsRestrictedNotification,
    onShowTimeshiftNotAvailableNotification,
    onSetTimeshiftNotification,
    onSwitchTimeshiftModeOn,
    onPauseLiveStream,
    onUpdateBookmark,
}: Params) {
    const dispatch = useDispatch();

    const { playbackType } = useSelector(selectPlayerConfig);
    const { getPlaybackResumeUavParams } = useGetPlaybackResumeUavParams({
        playbackType,
        videoItem,
    });
    const { getPlaybackPauseUavParams } = useGetPlaybackPauseUavParams({
        playbackType,
        videoItem,
    });

    const handlePause = useCallback(() => {
        logUavAnalytics(
            getPlaybackPauseUavParams({
                position: playerProgress,
            }),
        );
        setPlayerPausedByUser(true);
        setPlayerPaused(true);
        playerRef.current?.pause();

        if (isLive) {
            setReadyToPlay(false);

            if (timeshiftAllowed) {
                onSwitchTimeshiftModeOn(playerProgress, true);

                if (!startoverAllowed) {
                    onSetTimeshiftNotification();
                }
            } else if (startoverAllowed) {
                onPauseLiveStream(playerProgress);
            }
        } else {
            onUpdateBookmark();
        }
    }, [
        getPlaybackPauseUavParams,
        isLive,
        onPauseLiveStream,
        onSetTimeshiftNotification,
        onSwitchTimeshiftModeOn,
        onUpdateBookmark,
        playerProgress,
        playerRef,
        setPlayerPaused,
        setPlayerPausedByUser,
        setReadyToPlay,
        startoverAllowed,
        timeshiftAllowed,
    ]);

    const handlePlay = useCallback(() => {
        logUavAnalytics(
            getPlaybackResumeUavParams({
                templateId,
                railId,
            }),
        );
        setPlayerPausedByUser(false);
        setPlayerPaused(false);
        playerRef.current?.play();
        dispatch(startSentryTransaction(SENTRY_TRANSACTIONS.ResumeFromPause));
    }, [
        dispatch,
        getPlaybackResumeUavParams,
        playerRef,
        railId,
        setPlayerPaused,
        setPlayerPausedByUser,
        templateId,
    ]);

    return useCallback(() => {
        if (playerRef.current) {
            if (playerPaused) {
                handlePlay();
            } else if (accessIsRestricted(pauseAccessLevel)) {
                onShowControlsRestrictedNotification();
            } else if (isLive && isSOTSPauseDisabled) {
                onShowTimeshiftNotAvailableNotification();
            } else {
                handlePause();
            }
        }

        dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.ResumeFromPause));
    }, [
        playerRef,
        dispatch,
        playerPaused,
        pauseAccessLevel,
        isLive,
        isSOTSPauseDisabled,
        handlePlay,
        onShowControlsRestrictedNotification,
        onShowTimeshiftNotAvailableNotification,
        handlePause,
    ]);
}

export default usePlayerPlayPause;
